import React from 'react'
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Menu,
    Box,
    useTheme,
    TextField,
    InputAdornment,
} from '@material-ui/core'
import {
    Add as AddIcon,
    Refresh as RefreshIcon,
    GetApp as DownloadIcon,
    ExpandMore as ChevronDownIcon,
    Search as SearchIcon,
    LocalOffer as TagIcon,
    PlaylistAdd as BulkAddIcon,
} from '@material-ui/icons'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { makeStyles } from '@material-ui/core/styles'
import { UserTag } from '../../../../models/Users/types'

interface DashboardHeaderProps {
    timePeriod: string
    setTimePeriod: (value: string) => void
    customStartDate: Date | null
    customEndDate: Date | null
    onCustomDateChange: (startDate: Date | null, endDate: Date | null) => void
    selectedTagId: number | null
    setSelectedTagId: (value: number | null) => void
    availableTags: UserTag[]
    searchQuery: string
    setSearchQuery: (value: string) => void
    onRefresh: () => void
    onAddDeadline?: () => void
    onBulkDeadline?: () => void
}

const useStyles = makeStyles((theme) => ({
    appBar: {
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.appBar,
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
    },
    toolbar: {
        padding: theme.spacing(2, 4),
        minHeight: 72,
        flexWrap: 'wrap',
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1.5, 2),
            minHeight: 64,
        },
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(1, 1.5),
        },
    },
    logo: {
        width: 40,
        height: 40,
        borderRadius: theme.shape.borderRadius * 1.5,
        backgroundColor: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: theme.spacing(2),
        color: theme.palette.primary.contrastText,
        fontWeight: 'bold',
        fontSize: '1rem',
        boxShadow: theme.shadows[2],
    },
    titleContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    title: {
        fontSize: '1.5rem',
        fontWeight: 700,
        lineHeight: 1.2,
        color: theme.palette.text.primary,
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.25rem',
        },
    },
    subtitle: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.25),
    },
    controls: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(2),
        minWidth: 0,
        [theme.breakpoints.down('sm')]: {
            gap: theme.spacing(1),
        },
        [theme.breakpoints.down('xs')]: {
            width: '100%',
            flexBasis: '100%',
            justifyContent: 'flex-end',
        },
    },
    formControl: {
        minWidth: 140,
        maxWidth: 180,
        backgroundColor: theme.palette.background.default,
        [theme.breakpoints.down('sm')]: {
            minWidth: 120,
            maxWidth: 160,
        },
        [theme.breakpoints.down('xs')]: {
            minWidth: 100,
            maxWidth: '100%',
            flex: '1 1 120px',
        },
    },
    searchField: {
        minWidth: 180,
        maxWidth: 250,
        backgroundColor: theme.palette.background.default,
        [theme.breakpoints.down('sm')]: {
            minWidth: 140,
            maxWidth: 200,
        },
        [theme.breakpoints.down('xs')]: {
            minWidth: 0,
            flex: '1 1 100%',
            maxWidth: '100%',
        },
    },
    exportButton: {
        textTransform: 'none',
        padding: theme.spacing(0.75, 2),
        fontWeight: 500,
    },
    addDeadlineButton: {
        textTransform: 'none',
        fontWeight: 600,
        padding: theme.spacing(1, 2.5),
        borderRadius: 8,
        boxShadow: theme.shadows[2],
        flexShrink: 0,
        '&:hover': {
            boxShadow: theme.shadows[4],
        },
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(0.75, 1),
            minWidth: 'auto',
            '& .MuiButton-startIcon': { marginRight: 0, marginLeft: 0 },
            '& .MuiButton-label > *:not(.MuiButton-startIcon)': {
                display: 'none',
            },
        },
    },
    refreshButton: {
        padding: theme.spacing(1),
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    datePicker: {
        minWidth: 120,
        maxWidth: 180,
        backgroundColor: theme.palette.background.default,
        [theme.breakpoints.down('sm')]: {
            minWidth: 100,
            maxWidth: 140,
        },
        [theme.breakpoints.down('xs')]: {
            minWidth: 0,
            flex: '1 1 110px',
            maxWidth: '100%',
        },
    },
}))

export function DashboardHeader({
    timePeriod,
    setTimePeriod,
    customStartDate,
    customEndDate,
    onCustomDateChange,
    selectedTagId,
    setSelectedTagId,
    availableTags,
    searchQuery,
    setSearchQuery,
    onRefresh,
    onAddDeadline,
    onBulkDeadline,
}: DashboardHeaderProps) {
    const classes = useStyles()
    const theme = useTheme()

    const handleStartDateChange = (date: Date | null) => {
        onCustomDateChange(date, customEndDate)
    }

    const handleEndDateChange = (date: Date | null) => {
        onCustomDateChange(customStartDate, date)
    }

    const handleTagChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const value = event.target.value
        setSelectedTagId(value === 'all' ? null : (value as number))
    }

    const safeStartDate =
        customStartDate && !isNaN(customStartDate.getTime())
            ? customStartDate
            : null
    const safeEndDate =
        customEndDate && !isNaN(customEndDate.getTime()) ? customEndDate : null

    return (
        <AppBar position="sticky" className={classes.appBar} elevation={0}>
            <Toolbar className={classes.toolbar}>
                <Box
                    display="flex"
                    alignItems="center"
                    flex="1 1 auto"
                    minWidth={0}
                    style={{ marginRight: theme.spacing(2) }}
                >
                    <Box className={classes.titleContainer}>
                        <Typography
                            className={classes.title}
                            color="textPrimary"
                        >
                            Deadlines Dashboard
                        </Typography>
                    </Box>
                </Box>

                <Box className={classes.controls}>
                    {onAddDeadline && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onAddDeadline}
                            className={classes.addDeadlineButton}
                            startIcon={<AddIcon />}
                            title="Add deadline"
                        >
                            Add deadline
                        </Button>
                    )}
                    {onBulkDeadline && (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={onBulkDeadline}
                            className={classes.addDeadlineButton}
                            startIcon={<BulkAddIcon />}
                            title="Bulk deadline"
                        >
                            Bulk deadline
                        </Button>
                    )}
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Search properties..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={classes.searchField}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl
                        variant="outlined"
                        size="small"
                        className={classes.formControl}
                    >
                        <InputLabel id="tag-label">Property Tag</InputLabel>
                        <Select
                            labelId="tag-label"
                            id="tag-select"
                            value={selectedTagId ?? 'all'}
                            onChange={handleTagChange}
                            label="Property Tag"
                        >
                            <MenuItem value="all">All Properties</MenuItem>
                            {availableTags.map((tag) => (
                                <MenuItem key={tag.id} value={tag.id}>
                                    <Box display="flex" alignItems="center">
                                        <Box
                                            style={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: tag.color,
                                                marginRight: 8,
                                            }}
                                        />
                                        {tag.name}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl
                        variant="outlined"
                        size="small"
                        className={classes.formControl}
                    >
                        <InputLabel id="time-period-label">
                            Time Period
                        </InputLabel>
                        <Select
                            labelId="time-period-label"
                            id="time-period-select"
                            value={timePeriod}
                            onChange={(e) =>
                                setTimePeriod(e.target.value as string)
                            }
                            label="Time Period"
                        >
                            <MenuItem value="7days">Last 7 Days</MenuItem>
                            <MenuItem value="30days">Last 30 Days</MenuItem>
                            <MenuItem value="quarter">This Quarter</MenuItem>
                            <MenuItem value="year">This Year</MenuItem>
                            <MenuItem value="custom">Custom Range</MenuItem>
                        </Select>
                    </FormControl>

                    {timePeriod === 'custom' && (
                        <>
                            <KeyboardDatePicker
                                disabled={timePeriod !== 'custom'}
                                value={safeStartDate}
                                onChange={handleStartDateChange}
                                format="MM/dd/yyyy"
                                inputVariant="outlined"
                                label="Start Date"
                                size="small"
                                className={classes.datePicker}
                                maxDate={safeEndDate || undefined}
                                invalidDateMessage=""
                            />
                            <KeyboardDatePicker
                                disabled={timePeriod !== 'custom'}
                                value={safeEndDate}
                                onChange={handleEndDateChange}
                                format="MM/dd/yyyy"
                                inputVariant="outlined"
                                label="End Date"
                                size="small"
                                className={classes.datePicker}
                                minDate={safeStartDate || undefined}
                                invalidDateMessage=""
                            />
                        </>
                    )}

                    <IconButton
                        onClick={onRefresh}
                        className={classes.refreshButton}
                        aria-label="Refresh"
                        title="Refresh dashboard"
                    >
                        <RefreshIcon />
                    </IconButton>
                </Box>
            </Toolbar>
        </AppBar>
    )
}
